import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import {
  DEFAULT_FATURA_FILTERS,
  DEFAULT_FATURA_SUMMARY,
  type Fatura,
  type FaturaListFilters,
  type FaturaListSummary,
  type FaturasState,
} from '../types';
import {
  anexarComprovante,
  cancelarFatura,
  enviarSegundaVia,
  fetchFaturaById,
  fetchFaturas,
  gerarFaturaDeApuracao,
  gerarFaturaManual,
  gerarFaturasLote,
  reemitirFatura,
  tentarReemissaoAsaas,
} from './actions';

const initialState: FaturasState = {
  faturas: [],
  summary: DEFAULT_FATURA_SUMMARY,
  filters: DEFAULT_FATURA_FILTERS,
  loading: false,
  saving: false,
  error: null,
};

const computeSummary = (faturas: Fatura[]): FaturaListSummary => {
  return faturas.reduce(
    (acc, fatura) => {
      if (fatura.status === 'EMITIDA' || fatura.status === 'VENCIDA' || fatura.status === 'PAGA') {
        acc.totalEmitido += fatura.valorTotal;
      }
      if (fatura.status === 'PAGA') {
        acc.totalPago += fatura.valorPago ?? fatura.valorTotal;
      }
      if (fatura.status === 'EMITIDA' || fatura.status === 'VENCIDA') {
        acc.totalAReceber += fatura.valorTotal;
      }
      if (fatura.status === 'VENCIDA') {
        acc.totalVencido += fatura.valorTotal;
      }
      if (fatura.status === 'ERRO_EMISSAO') {
        acc.quantidadeComErroEmissao += 1;
      }
      return acc;
    },
    { ...DEFAULT_FATURA_SUMMARY },
  );
};

const upsertFatura = (state: FaturasState, fatura: Fatura) => {
  const idx = state.faturas.findIndex((f) => f.id === fatura.id);
  if (idx >= 0) {
    state.faturas[idx] = fatura;
  } else {
    state.faturas.unshift(fatura);
  }
};

const faturasSlice = createSlice({
  name: 'faturas',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<Partial<FaturaListFilters>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = DEFAULT_FATURA_FILTERS;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFaturas.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFaturas.fulfilled, (state, action: PayloadAction<Fatura[]>) => {
        state.loading = false;
        state.faturas = action.payload;
        state.summary = computeSummary(action.payload);
      })
      .addCase(fetchFaturas.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) ?? 'Erro ao carregar faturas';
      })

      .addCase(fetchFaturaById.fulfilled, (state, action: PayloadAction<Fatura>) => {
        upsertFatura(state, action.payload);
        state.summary = computeSummary(state.faturas);
      })

      .addCase(gerarFaturaManual.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(gerarFaturaManual.fulfilled, (state, action: PayloadAction<Fatura>) => {
        state.saving = false;
        upsertFatura(state, action.payload);
        state.summary = computeSummary(state.faturas);
      })
      .addCase(gerarFaturaManual.rejected, (state, action) => {
        state.saving = false;
        state.error = (action.payload as string) ?? 'Erro ao gerar fatura manual';
      })

      .addCase(gerarFaturaDeApuracao.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(gerarFaturaDeApuracao.fulfilled, (state, action: PayloadAction<Fatura>) => {
        state.saving = false;
        upsertFatura(state, action.payload);
        state.summary = computeSummary(state.faturas);
      })
      .addCase(gerarFaturaDeApuracao.rejected, (state, action) => {
        state.saving = false;
        state.error = (action.payload as string) ?? 'Erro ao gerar fatura a partir da apuração';
      })

      .addCase(gerarFaturasLote.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(
        gerarFaturasLote.fulfilled,
        (
          state,
          action: PayloadAction<{
            sucesso: Fatura[];
            falhas: Array<{ apuracaoId: string; motivo: string }>;
          }>,
        ) => {
          state.saving = false;
          action.payload.sucesso.forEach((fatura) => upsertFatura(state, fatura));
          state.summary = computeSummary(state.faturas);
        },
      )
      .addCase(gerarFaturasLote.rejected, (state, action) => {
        state.saving = false;
        state.error = (action.payload as string) ?? 'Erro ao gerar faturas em lote';
      })

      .addCase(cancelarFatura.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(cancelarFatura.fulfilled, (state, action: PayloadAction<Fatura>) => {
        state.saving = false;
        upsertFatura(state, action.payload);
        state.summary = computeSummary(state.faturas);
      })
      .addCase(cancelarFatura.rejected, (state, action) => {
        state.saving = false;
        state.error = (action.payload as string) ?? 'Erro ao cancelar fatura';
      })

      .addCase(reemitirFatura.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(
        reemitirFatura.fulfilled,
        (state, action: PayloadAction<{ original: Fatura; nova: Fatura }>) => {
          state.saving = false;
          upsertFatura(state, action.payload.original);
          upsertFatura(state, action.payload.nova);
          state.summary = computeSummary(state.faturas);
        },
      )
      .addCase(reemitirFatura.rejected, (state, action) => {
        state.saving = false;
        state.error = (action.payload as string) ?? 'Erro ao reemitir fatura';
      })

      .addCase(enviarSegundaVia.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(enviarSegundaVia.fulfilled, (state, action: PayloadAction<Fatura>) => {
        state.saving = false;
        upsertFatura(state, action.payload);
      })
      .addCase(enviarSegundaVia.rejected, (state, action) => {
        state.saving = false;
        state.error = (action.payload as string) ?? 'Erro ao enviar 2ª via';
      })

      .addCase(anexarComprovante.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(anexarComprovante.fulfilled, (state, action: PayloadAction<Fatura>) => {
        state.saving = false;
        upsertFatura(state, action.payload);
        state.summary = computeSummary(state.faturas);
      })
      .addCase(anexarComprovante.rejected, (state, action) => {
        state.saving = false;
        state.error = (action.payload as string) ?? 'Erro ao anexar comprovante';
      })

      .addCase(tentarReemissaoAsaas.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(tentarReemissaoAsaas.fulfilled, (state, action: PayloadAction<Fatura>) => {
        state.saving = false;
        upsertFatura(state, action.payload);
        state.summary = computeSummary(state.faturas);
      })
      .addCase(tentarReemissaoAsaas.rejected, (state, action) => {
        state.saving = false;
        state.error = (action.payload as string) ?? 'Erro ao tentar reemissão no Asaas';
      });
  },
});

export const { setFilters, clearFilters, clearError } = faturasSlice.actions;
export const faturasReducer = faturasSlice.reducer;
